import type { TeamType } from '@modules/Auth/models/Auth';
import { useAuthStore } from '@shared/stores/authStore';
import { useCallback, useMemo } from 'react';

/**
 * Hook que expone las funciones de permisos del authStore de manera reactiva
 *
 * @example
 * ```tsx
 * const { canAccess, hasPermission } = usePermissions();
 * const canCreate = canAccess(['tenant'], ['products.create']);
 * ```
 */
export function usePermissions() {
  const store = useAuthStore();
  const isBlockModel = store.authorization?.model_version === 2 || store.role?.model_version === 2;
  // TEMPORARY (legacy admin access): CUSTOMER_HERO_LEGACY_ADMIN_ACCESS. On model 1 the back lists the Customer Hero grants
  // of a 3PL owner/Admin in role.permissions; the store drops `access.*` slugs on model 1, so they are honoured here.
  // Memoised: consumers use these functions as effect dependencies, and a new identity per render loops (React #185).
  const rolePermissions = store.role?.permissions;
  const legacyCustomerHero = useMemo(
    () =>
      isBlockModel
        ? []
        : (rolePermissions ?? []).map((p) => p.slug).filter((slug) => slug.startsWith('access.customer-hero.')),
    [isBlockModel, rolePermissions],
  );
  const {
    hasPermission: storeHasPermission,
    getUserPermissions: storeUserPermissions,
    canAccess: storeCanAccess,
  } = store;
  const hasPermission = useCallback(
    (slug: string) => storeHasPermission(slug) || legacyCustomerHero.includes(slug),
    [storeHasPermission, legacyCustomerHero],
  );
  const hasAnyPermission = useCallback(
    (slugs: string[]) => slugs.length === 0 || slugs.some(hasPermission),
    [hasPermission],
  );
  const hasAllPermissions = useCallback((slugs: string[]) => slugs.every(hasPermission), [hasPermission]);
  const getUserPermissions = useCallback(
    () => [...storeUserPermissions(), ...legacyCustomerHero],
    [storeUserPermissions, legacyCustomerHero],
  );
  const canAccess = useCallback(
    (teamTypes?: TeamType[], permissions?: string[]) =>
      storeCanAccess(teamTypes, []) && (!permissions?.length || hasAnyPermission(permissions)),
    [storeCanAccess, hasAnyPermission],
  );

  return {
    // 🎯 Obtener el tipo de team del usuario
    getTeamType: store.getTeamType,

    // 🎯 Obtener todos los permisos del usuario
    getUserPermissions,

    // 🎯 Verificar un permiso específico
    hasPermission,

    // 🎯 Verificar si tiene al menos uno de los permisos
    hasAnyPermission,

    // 🎯 Verificar si tiene todos los permisos
    hasAllPermissions,

    // 🎯 Verificación combinada: team_type + permisos
    canAccess,

    // 🎯 Verificar si el usuario tiene rol de admin
    isAdminRole: store.isAdminRole,

    // 🎯 Verificar si wallet_visible está habilitado (para tenant_external)
    isWalletVisible: store.isWalletVisible,

    // 🎯 Proveedor contable del tenant (holded | contalink)
    getAccountingSupplier: store.getAccountingSupplier,
    isHoldedAccounting: store.isHoldedAccounting,
    isContalinkAccounting: store.isContalinkAccounting,
    isBlockModel,
    // TEMPORARY (legacy admin access): Customer Hero screens accept v2 or the legacy grants above; on removal rename
    // every isCustomerHeroModel back to isBlockModel.
    isCustomerHeroModel: isBlockModel || legacyCustomerHero.length > 0,
    authorizationScope: store.getAuthorizationScope(),

    // 🎯 Datos de contexto (útil para debugging)
    teamType: store.team?.type ?? null,
    permissions: store.role?.permissions ?? [],
  };
}
